import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import api from '../lib/api';
import { ArrowLeft, Layers, Clock, TrendingUp, Target, Unlock, Sparkles, Activity } from 'lucide-react';

export default function HistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [learning, setLearning] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/game/sessions'),
      api.get('/game/learning'),
    ])
      .then(([sessRes, learnRes]) => {
        setSessions(sessRes.data);
        setLearning(learnRes.data);
      })
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, []);

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-zinc-400 font-mono text-sm uppercase tracking-widest">Loading history...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="history-page">
      {/* Header */}
      <header className="flex items-center gap-4 px-4 py-3 border-b border-zinc-200 bg-white">
        <Button variant="ghost" size="sm" onClick={() => navigate('/game')} data-testid="back-to-game-btn">
          <ArrowLeft size={16} className="mr-1" /> Back to Field
        </Button>
        <h1 className="text-lg font-bold tracking-tight">History & Learning</h1>
      </header>

      <div className="max-w-5xl mx-auto p-6 md:p-10 space-y-8">
        {/* Learning Stats */}
        {learning && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-zinc-200 shadow-none rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-widest font-mono mb-2">
                  <Target size={14} /> Total Attempts
                </div>
                <div className="text-3xl font-bold font-mono" data-testid="stat-attempts">{learning.total_attempts}</div>
              </CardContent>
            </Card>
            <Card className="border-zinc-200 shadow-none rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-widest font-mono mb-2">
                  <TrendingUp size={14} /> Difficulty
                </div>
                <div className="text-3xl font-bold font-mono" data-testid="stat-difficulty">{(learning.difficulty_modifier * 100).toFixed(0)}%</div>
              </CardContent>
            </Card>
            <Card className="border-zinc-200 shadow-none rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-widest font-mono mb-2">
                  <Layers size={14} /> Best Rounds
                </div>
                <div className="text-3xl font-bold font-mono" data-testid="stat-best">{learning.best_round_count}</div>
              </CardContent>
            </Card>
            <Card className="border-zinc-200 shadow-none rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-widest font-mono mb-2">
                  <Target size={14} /> Equilibriums Reached
                </div>
                <div className="text-3xl font-bold font-mono" data-testid="stat-equilibriums">{learning.total_equilibriums || 0}</div>
                <div className="text-xs text-zinc-500 mt-2">
                  Unlocks new tools as you progress
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Preferred Shapes */}
        {learning?.preferred_shapes && Object.keys(learning.preferred_shapes).length > 0 && (
          <Card className="border-zinc-200 shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Shape Preferences (Learned)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-3 h-24">
                {Object.entries(learning.preferred_shapes)
                  .sort((a, b) => b[1] - a[1])
                  .map(([shape, count]) => {
                    const maxCount = Math.max(...Object.values(learning.preferred_shapes));
                    const heightPct = (count / maxCount) * 100;
                    return (
                      <div key={shape} className="flex flex-col items-center gap-1 flex-1">
                        <div
                          className="w-full bg-zinc-900 rounded-sm transition-all"
                          style={{ height: `${heightPct}%`, minHeight: '4px' }}
                        />
                        <span className="text-xs font-mono text-zinc-500 truncate w-full text-center">{shape}</span>
                        <span className="text-xs font-mono text-zinc-400">{count}</span>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Aggregate Pattern Analysis - learn from all sessions */}
        {sessions.length > 0 && (
          <Card className="border-zinc-200 shadow-none rounded-sm">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Activity size={16} /> Pattern Analysis (Learned)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                // Calculate average pattern scores across all sessions
                const sessionsWithPatterns = sessions.filter(s => s.pattern_scores);
                if (sessionsWithPatterns.length === 0) {
                  return (
                    <p className="text-sm text-zinc-400 font-mono">
                      Pattern data will appear after your next session.
                    </p>
                  );
                }
                const avgPatterns = {
                  density: sessionsWithPatterns.reduce((sum, s) => sum + (s.pattern_scores?.density || 0), 0) / sessionsWithPatterns.length,
                  symmetry: sessionsWithPatterns.reduce((sum, s) => sum + (s.pattern_scores?.symmetry || 0), 0) / sessionsWithPatterns.length,
                  linearity: sessionsWithPatterns.reduce((sum, s) => sum + (s.pattern_scores?.linearity || 0), 0) / sessionsWithPatterns.length,
                  regularity: sessionsWithPatterns.reduce((sum, s) => sum + (s.pattern_scores?.regularity || 0), 0) / sessionsWithPatterns.length,
                };
                const equilibriumCount = sessions.filter(s => s.equilibrium_reached).length;
                
                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold font-mono text-zinc-900">{Math.round(avgPatterns.density * 100)}%</div>
                        <div className="text-xs text-zinc-400 font-mono uppercase">Avg Density</div>
                        <div className="w-full bg-zinc-100 h-1.5 rounded mt-2">
                          <div className="bg-zinc-900 h-full rounded" style={{ width: `${avgPatterns.density * 100}%` }} />
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold font-mono text-zinc-900">{Math.round(avgPatterns.symmetry * 100)}%</div>
                        <div className="text-xs text-zinc-400 font-mono uppercase">Avg Symmetry</div>
                        <div className="w-full bg-zinc-100 h-1.5 rounded mt-2">
                          <div className="bg-blue-500 h-full rounded" style={{ width: `${avgPatterns.symmetry * 100}%` }} />
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold font-mono text-zinc-900">{Math.round(avgPatterns.linearity * 100)}%</div>
                        <div className="text-xs text-zinc-400 font-mono uppercase">Avg Linearity</div>
                        <div className="w-full bg-zinc-100 h-1.5 rounded mt-2">
                          <div className="bg-emerald-500 h-full rounded" style={{ width: `${avgPatterns.linearity * 100}%` }} />
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold font-mono text-zinc-900">{Math.round(avgPatterns.regularity * 100)}%</div>
                        <div className="text-xs text-zinc-400 font-mono uppercase">Avg Regularity</div>
                        <div className="w-full bg-zinc-100 h-1.5 rounded mt-2">
                          <div className="bg-purple-500 h-full rounded" style={{ width: `${avgPatterns.regularity * 100}%` }} />
                        </div>
                      </div>
                    </div>
                    {equilibriumCount > 0 && (
                      <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
                        <Sparkles size={14} className="text-amber-500" />
                        <span className="text-sm font-mono text-zinc-600">
                          Equilibrium reached: <span className="font-bold">{equilibriumCount}</span> time{equilibriumCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        <Separator />

        {/* Sessions Table */}
        <div>
          <h2 className="text-xl font-semibold tracking-tight mb-4">Past Sessions</h2>
          {sessions.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 font-mono text-sm">
              No sessions yet. Enter the field to begin.
            </div>
          ) : (
            <Card className="border-zinc-200 shadow-none rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-mono text-xs uppercase">Date</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Rounds</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Max Conf.</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Pieces</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Pattern</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Duration</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => (
                    <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                      <TableCell className="font-mono text-sm">
                        {new Date(s.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-mono text-sm font-bold">{s.rounds_survived}</TableCell>
                      <TableCell className="font-mono text-sm">{Math.round(s.max_confidence)}%</TableCell>
                      <TableCell className="font-mono text-sm">{s.pieces?.length || 0}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {s.pattern_scores ? (
                          <div className="flex gap-1">
                            <span title="Symmetry" className="text-blue-500">S:{Math.round((s.pattern_scores.symmetry || 0) * 100)}</span>
                            <span title="Linearity" className="text-emerald-500">L:{Math.round((s.pattern_scores.linearity || 0) * 100)}</span>
                            <span title="Regularity" className="text-purple-500">R:{Math.round((s.pattern_scores.regularity || 0) * 100)}</span>
                          </div>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{formatDuration(s.duration_ms)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Badge variant={s.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                            {s.status}
                          </Badge>
                          {s.equilibrium_reached && (
                            <Sparkles size={12} className="text-amber-500" title="Equilibrium reached" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
